import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Button, Column, Row, Tooltip, Typography } from '@tempmee/react';
import {
  OutlinePlusIcon,
  SolidInformationCircleIcon,
} from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { addDays } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useController, useForm, useWatch } from 'react-hook-form';
import { LeaveInterviewCreationPopup } from '~/commonComponents/popups/LeaveInterviewCreationPopup';
import { CalendarPicker, FormActions } from '~/components/shared/forms';
import { TipBanner } from '~/components/shared/TipBanner';
import { SectionCard } from '~/components/ShiftRequestRedesign/common';
import { PreferredSpecialtyField } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/fields/PreferredSpecialtyField';
import { ProfessionalPayField } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/fields/ProfessionalPayField';
import { TimeFieldsWrapper } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/fields/TimeFieldsWrapper';
import { SelectedDatesList } from '~/components/ShiftRequestRedesign/SelectDates/SelectedDatesList';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import {
  useIsParentOfficeSelector,
  useNavigationDetector,
  useToggle,
} from '~/hooks';
import { useUserSelector } from '~/hooks/useUserSelector';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';
import { InvitePopup } from '../../InviteCandidate/components/InvitePopup';
import { createOnsiteInterviewFormRulesV2, TOnsiteInterviewFormData } from './FormRule';
import { OnsiteInterviewFormFields } from './OnsiteInterviewForm';
import { SelectedCandidatesList } from './SelectedCandidatesList';
import { useOnsiteInterviewFormSubmitV2 } from './useOnsiteInterviewFormSubmitV2';

interface IOnsiteInterviewFormV2Props {
  jobId: string;
  jobTitle: string;
  /** The office id (for parent offices) or office name (for private practices). */
  office: string;
  /** Profession code (e.g. "RDH") — resolved to display name for the specialty dropdown. */
  profession: string;
  btnSecondaryText?: string;
  defaultValues?: TOnsiteInterviewFormData;
  isAfterJobPosting?: boolean;
  isEdit?: boolean;
  readonlyFields?: OnsiteInterviewFormFields[];
  unavailableDates?: Date[];
  btnSecondaryClick?: () => void;
  /**
   * Called whenever the form's `isDirty` flag flips. Lets the parent decide
   * which leave-confirmation popup to show (Unsaved Changes when dirty,
   * Wait Before You Go when clean) without duplicating the dirty tracking.
   */
  onIsDirtyChange?: (isDirty: boolean) => void;
  onSubmit(formData: TOnsiteInterviewFormData): void;
}

const EdtBadge = () => (
  <Box
    style={{
      backgroundColor: colors.neutral[400],
      borderRadius: 10,
      paddingInline: 6,
      height: 15,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Typography size="caption_sm" weight="bold" color="#fff">
      EDT
    </Typography>
  </Box>
);

export const OnsiteInterviewFormV2 = ({
  btnSecondaryText,
  defaultValues,
  isAfterJobPosting = false,
  isEdit = false,
  jobId,
  jobTitle,
  office,
  profession,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  readonlyFields = [],
  unavailableDates = [],
  btnSecondaryClick,
  onIsDirtyChange,
  onSubmit,
}: IOnsiteInterviewFormV2Props) => {
  // V2 defaults the time inputs to a standard 8am-12pm window per Figma.
  const mergedDefaults = useMemo<TOnsiteInterviewFormData | undefined>(() => {
    if (!defaultValues) return defaultValues;
    return {
      ...defaultValues,
      hoursArrival: defaultValues.hoursArrival || '08:00 am',
      hoursDeparture: defaultValues.hoursDeparture || '12:00 pm',
    };
  }, [defaultValues]);

  const timeLimitInMinutes = RemoteConfigUtils.getOnsiteInterviewThresholdInMinutes();
  const timeLimitInHours = Math.round(timeLimitInMinutes / 60);

  const schema = useMemo(
    () => createOnsiteInterviewFormRulesV2(timeLimitInMinutes),
    [timeLimitInMinutes],
  );

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    setValue,
  } = useForm<TOnsiteInterviewFormData>({
    defaultValues: mergedDefaults,
    mode: 'all',
    resolver: zodResolver(schema) as any,
  });

  const [showUnsavedChangesPopup, setShowUnsavedChangesPopup] = useState(false);

  const { allowNavigation, clearPendingNavigation, navigateToPending } = useNavigationDetector({
    when: isDirty,
    onNavigationAttempt: () => setShowUnsavedChangesPopup(true),
  });

  useEffect(() => {
    onIsDirtyChange?.(isDirty);
  }, [isDirty, onIsDirtyChange]);

  const {
    bannerRef,
    exceedsLimitMessage,
    fieldErrors,
    markAttemptedSubmit,
    showExceedsLimitBanner,
    showMissingRequiredBanner,
  } = useOnsiteInterviewFormSubmitV2({ control, timeLimitInMinutes });

  const isButtonDisabled = isEdit ? !isDirty || !isValid : !isValid;

  const handleNextPressed = useCallback(() => {
    markAttemptedSubmit();
    if (isButtonDisabled) return;
    handleSubmit((formData: TOnsiteInterviewFormData) => {
      allowNavigation();
      onSubmit(formData);
    })();
  }, [allowNavigation, handleSubmit, isButtonDisabled, markAttemptedSubmit, onSubmit]);

  const isParentOffice = useIsParentOfficeSelector();
  const currentUser = useUserSelector();

  const [isInvitePopupOpen, invitePopupControls] = useToggle();

  const { field: specialtyField } = useController({ control, name: 'specialty' });
  const { field: arrivalField } = useController({ control, name: 'hoursArrival' });
  const { field: departureField } = useController({ control, name: 'hoursDeparture' });
  const { field: rateField } = useController({ control, name: 'hourlyRate' });

  const watchDates = useWatch({ control, name: 'dates' }) ?? [];
  const selectedCount = watchDates.length;
  const tipVisible = selectedCount < 2;
  const nextButtonText = isEdit
    ? 'Save Changes'
    : `Next (${selectedCount} ${selectedCount === 1 ? 'Day' : 'Days'} Selected)`;

  const today = new Date();

  const professionDisplayName = useMemo(() => {
    if (!profession) return '';
    return (
      UserProfession.getName(profession) ||
      UserSubProfession.getName(profession) ||
      profession
    );
  }, [profession]);

  const rateNumericValue = useMemo(() => {
    if (rateField.value == null || rateField.value === '') return null;
    const parsed = parseFloat(rateField.value);
    return Number.isNaN(parsed) ? null : parsed;
  }, [rateField.value]);

  const handleRateChange = useCallback(
    (v: number) => {
      rateField.onChange(v != null ? `${v}.00` : '');
    },
    [rateField],
  );

  const effectiveOfficeId = isParentOffice ? office : (currentUser?.id ?? '');

  return (
    <Column gap={6} style={{ width: '100%' }}>
      <LeaveInterviewCreationPopup
        isOpen={showUnsavedChangesPopup}
        onDiscard={() => {
          setShowUnsavedChangesPopup(false);
          allowNavigation();
          navigateToPending();
        }}
        onKeepEditing={() => {
          setShowUnsavedChangesPopup(false);
          clearPendingNavigation();
        }}
      />

      {(showMissingRequiredBanner || showExceedsLimitBanner) && (
        <Column gap={0}>
          {showMissingRequiredBanner && (
            <div ref={bannerRef} role="alert" aria-live="polite" data-testid="form-error-banner">
              <Alert
                severity="error"
                style={{ border: 'none', backgroundColor: 'transparent', paddingInline: 0 }}
                description="Oops! It looks like a few required fields were left empty."
              />
            </div>
          )}

          {showExceedsLimitBanner && (
            <div
              ref={showMissingRequiredBanner ? undefined : bannerRef}
              role="alert"
              aria-live="polite"
              data-testid="form-error-banner-exceeds-limit"
            >
              <Alert
                severity="error"
                style={{ border: 'none', backgroundColor: 'transparent', paddingInline: 0 }}
                description={exceedsLimitMessage}
              />
            </div>
          )}
        </Column>
      )}


      <SectionCard title="Interview Dates" testId="interview-dates-card" collapsible>
        <Box style={{ marginBottom: 12 }}>
          <TipBanner
            visible={tipVisible}
            description="Select all days you'd like to post onsite interviews. Jobs with more interview dates receive more candidates."
          />
        </Box>

        <Controller
          control={control}
          name="dates"
          render={({ field }) => (
            <>
              <Box style={{ display: 'flex', justifyContent: 'center' }}>
                <CalendarPicker
                  data-testid="interview-dates-calendar"
                  data-cy="interview-dates-calendar"
                  mode="multiple"
                  numberOfMonths={2}
                  selected={field.value ?? []}
                  onSelect={(dates) => {
                    field.onChange(dates ?? []);
                  }}
                  startMonth={today}
                  disabled={[
                    {
                      before: today,
                      after: addDays(today, 99),
                    },
                    ...(unavailableDates.map((d) => ({ from: d, to: d })) as any),
                  ]}
                  hasError={fieldErrors.dates}
                />
              </Box>

              <Box style={{ marginTop: 20 }}>
                <SelectedDatesList
                  dates={field.value ?? []}
                  onRemoveDate={(date) => {
                    field.onChange(
                      (field.value ?? []).filter((d) => d.getTime() !== date.getTime()),
                    );
                  }}
                />
              </Box>
            </>
          )}
        />
      </SectionCard>

      <SectionCard title="Interview Details" testId="interview-details-card">
        {isParentOffice && (
          <Column gap={2} style={{ marginBottom: 20 }} data-testid="office-field" data-cy="office-field">
            <Typography variant="subtitle1" weight="bold">
              Office:
            </Typography>
            <Typography variant="body1">{office}</Typography>
          </Column>
        )}

        <Column gap={2} style={{ marginBottom: 20 }} data-testid="job-title-field" data-cy="job-title-field">
          <Typography variant="subtitle1" weight="bold">
            Job Title:
          </Typography>
          <Typography variant="body1">{jobTitle}</Typography>
        </Column>

        <PreferredSpecialtyField
          value={specialtyField.value}
          onChange={specialtyField.onChange}
          professionType={professionDisplayName}
          hasError={fieldErrors.specialty}
        />

        <Typography size="heading_3" weight="bold" style={{ marginTop: 24 }}>
          Hours &amp; Pay
        </Typography>
        <Box
          style={{
            height: 1.5,
            backgroundColor: colors.neutral[100],
            marginTop: 10,
            marginBottom: 18,
          }}
        />

        <Column gap={2}>
          <TimeFieldsWrapper
            arrivalValue={arrivalField.value ?? ''}
            onArrivalChange={(v) => {
              arrivalField.onChange(v);
              setValue('hoursArrival', v, { shouldValidate: true, shouldDirty: true });
            }}
            departureValue={departureField.value ?? ''}
            onDepartureChange={(v) => {
              departureField.onChange(v);
              setValue('hoursDeparture', v, { shouldValidate: true, shouldDirty: true });
            }}
            labelRight={<EdtBadge />}
            hasArrivalError={fieldErrors.hoursArrival}
            hasDepartureError={fieldErrors.hoursDeparture}
            helperText={null}
          />
          <Typography variant="body1" color={colors.neutral[500]}>
            Onsite interviews can run up to {timeLimitInHours} hours, giving you the time you need
            to evaluate skills and overall fit.
          </Typography>
        </Column>

        <Box style={{ marginTop: 20 }}>
          <ProfessionalPayField
            value={rateNumericValue}
            onChange={handleRateChange}
            professionType={professionDisplayName}
            officeId={effectiveOfficeId}
            hasError={fieldErrors.hourlyRate}
          />
        </Box>
      </SectionCard>

      {!isEdit && !isAfterJobPosting && (
        <Controller
          control={control}
          name="professionals"
          render={({ field }) => (
            <>
              <SectionCard
                testId="invites-card"
                title={
                  <Row align="center" gap={2}>
                    <Typography size="heading_3" weight="bold">
                      Invites
                    </Typography>
                    <Tooltip
                      placement="bottom"
                      title={
                        <Typography color="#fff" weight="bold">
                          Please note, invites will not be delivered to professionals on requested
                          dates in which they are unavailable.
                        </Typography>
                      }
                    >
                      <SolidInformationCircleIcon color={colors.neutral[300]} />
                    </Tooltip>
                  </Row>
                }
                headerRight={
                  <Button
                    variant="contained"
                    size="xs"
                    LeadingIcon={<OutlinePlusIcon color="#fff" />}
                    data-testid="invites-add-candidates-button"
                    data-cy="invites-add-candidates-button"
                    onClick={invitePopupControls.setOn}
                  >
                    Add Candidates
                  </Button>
                }
              >
                {(field.value ?? []).length > 0 ? (
                  <SelectedCandidatesList
                    candidates={field.value ?? []}
                    onRemove={(value) =>
                      field.onChange((field.value ?? []).filter((c) => c.value !== value))
                    }
                  />
                ) : (
                  <TipBanner
                    visible
                    description="Invite professionals you've worked with before to apply directly to this interview."
                  />
                )}
              </SectionCard>

              <InvitePopup
                isOpen={isInvitePopupOpen}
                controls={invitePopupControls}
                jobPostingId={jobId}
                value={(field.value ?? []).map((it) => it.value)}
                onConfirm={(it) => {
                  field.onChange(it.map(({ fullName, id }) => ({ label: fullName, value: id })));
                  invitePopupControls.setOff();
                }}
              />
            </>
          )}
        />
      )}

      <FormActions>
        <Column align="center" gap={4}>
          <Button
            data-testid="onsite-interview-form-v2-next"
            data-cy="onsite-interview-form-v2-next"
            onClick={handleNextPressed}
          >
            {nextButtonText}
          </Button>
          {btnSecondaryClick && btnSecondaryText && (
            <Box
              data-testid="onsite-interview-form-v2-secondary"
              data-cy="onsite-interview-form-v2-secondary"
              onClick={btnSecondaryClick}
              style={{ cursor: 'pointer' }}
            >
              <Typography size="heading_4" weight="bold" color={colors.secondary[500]}>
                {btnSecondaryText}
              </Typography>
            </Box>
          )}
        </Column>
      </FormActions>
    </Column>
  );
};
